import { Box, Popover, Tooltip } from '@mui/material';
import { Styles } from 'common/types';
import { theme } from 'common/constants/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactElement, useRef, useState } from 'react';
import { SelectOption } from '../select-field/select-field';

const getStatusColor = (status: string, disabled?: boolean) => {
  if (disabled) return theme.palette.grey[500];

  switch (status) {
    case 'pending':
    case 'paused':
      return theme.palette.warning.main;
    case 'active':
    case 'enabled':
    case 'added':
    case 'In Article':
    case 'accepted':
      return '#62D294';
    case 'error':
    case 'failed':
    case 'disabled':
    case 'inactive':
    case 'Removed from Article':
      return theme.palette.error.main;
    case 'finished':
      return theme.palette.success.light;
    case 'available':
    case 'Not In Article':
      return theme.palette.grey[500];
    default:
      return theme.palette.primary.main;
  }
};

interface StylesProps {
  status: string;
  color: string;
  pointer: boolean;
  disabled?: boolean;
  hideValueText?: boolean;
  size: 'small' | 'large';
}

const getStyles = ({ status, pointer, color, disabled, hideValueText, size }: StylesProps): Styles => ({
  container: { display: 'flex', alignItems: 'center', cursor: pointer ? 'pointer' : 'default' },
  status: {
    padding: '5px',
    minWidth: hideValueText ? 10 : 80,
    fontWeight: 300,
    boxSizing: 'border-box',
    color: '#fff',
    backgroundColor: color ?? getStatusColor(status, disabled),
    borderRadius: '20px',
    textTransform: 'capitalize',
    textAlign: 'center',
    fontSize: size === 'large' ? 14 : 12,
  },
  optionsContainer: { display: 'flex', flexDirection: 'column', width: 100 },
  option: {
    padding: 1,
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    fontWeight: 400,
    fontSize: 14,
    cursor: 'pointer',
  },
});

interface Props {
  value: string;
  color?: string;
  showExpand?: boolean;
  options?: Array<SelectOption>;
  tooltip?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  disabled?: boolean;
  hideValueText?: boolean;
  size?: 'small' | 'large';
}

export function StatusDropdown({
  value,
  options,
  color,
  onChange,
  showExpand = true,
  tooltip,
  disabled,
  hideValueText,
  size = 'large',
}: Props): ReactElement {
  const styles = getStyles({
    status: value,
    pointer: options?.length && !disabled,
    color,
    disabled,
    hideValueText,
    size,
  });

  const [statusAnchor, setStatusAcnhor] = useState<HTMLDivElement | null>(null);

  const statusRef = useRef<HTMLDivElement>();

  const handleStatusClick = () => {
    if (!options?.length || disabled) return;
    setStatusAcnhor(statusRef.current);
  };

  const handlePopoverClose = () => {
    setStatusAcnhor(null);
  };

  return (
    <>
      <Box sx={styles.container} onClick={handleStatusClick}>
        <Tooltip title={tooltip ?? ''} placement="top" disableHoverListener={!tooltip}>
          <Box sx={styles.status} ref={statusRef}>
            {!hideValueText ? value : ''}
          </Box>
        </Tooltip>
        {showExpand && <ExpandMoreIcon fontSize="small" />}
      </Box>
      {Boolean(options?.length) && (
        <Popover
          open={Boolean(statusAnchor)}
          anchorEl={statusAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={handlePopoverClose}
        >
          <Box sx={styles.optionsContainer}>
            {options
              .filter(o => o.value !== value)
              .map(o => (
                <Box
                  key={o.value}
                  sx={styles.option}
                  onClick={() => {
                    if (onChange) onChange(o.value);
                    handlePopoverClose();
                  }}
                >
                  {o.title}
                </Box>
              ))}
          </Box>
        </Popover>
      )}
    </>
  );
}
